import { BlockLoading } from '../fetch-status/block-loading';
import { BlockNothing } from '../fetch-status/block-nothing';
import { EndStatus } from '../fetch-status/end-status';
import { RepliesItem } from './replies-item';
import { IS_DEBUG, REFETCH_INTERVAL_MEDIUM } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useComments } from '@/libs/react-sdk/api/use-comments';
import { CommentReplies, CommentsParams } from '@/libs/sdk/api';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RepliesListProps = {
  className?: string;
  postUuid: string;
  rootUuid?: string;
  lastId?: string;
  replies?: CommentReplies[];
  repliesTotalCount?: string;
};
export const RepliesList = ({ className, postUuid, rootUuid, lastId, replies, repliesTotalCount }: RepliesListProps) => {
  const { t } = useTranslation();
  const [loadMore, setLoadMore] = useState(false);
  const isRootComment = useMemo(() => !rootUuid, [rootUuid]);
  const enableFetch = useMemo(() => isRootComment || loadMore, [isRootComment, loadMore]);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const searchParams = useMemo(() => {
    const params = { page: 1, postUuid, baseTokenAddress } as CommentsParams;
    if (lastId) params.lastId = lastId;
    if (rootUuid) {
      params.rootUuid = rootUuid;
      params.pageSize = 3;
      if (replies && replies?.length > 0) params.lastId = replies?.[replies.length - 1]?.id;
    } else {
      params.replyPageSize = 1;
      params.pageSize = 10;
    }
    return params;
  }, [baseTokenAddress, lastId, postUuid, replies, rootUuid]);
  const { data, isLoading, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useComments(api, searchParams, {
    enabled: enableFetch,
    refetchInterval: REFETCH_INTERVAL_MEDIUM,
  });
  const hasMore = useMemo(() => {
    if (enableFetch) return hasNextPage;
    return repliesTotalCount && replies && replies?.length < Number(repliesTotalCount);
  }, [enableFetch, hasNextPage, replies, repliesTotalCount]);
  const commentList = useMemo(() => data?.pages.flatMap(item => item.comments) ?? [], [data?.pages]);
  const moreLastIndex = useMemo(() => commentList.length - 5, [commentList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasMore && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more comments post', moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div
      className={cn(
        'w-full overflow-y-auto overflow-x-hidden',
        isRootComment ? 'divide-y-1 divide-foreground/4' : 'ps-9',
        className
      )}
    >
      {replies?.map(item => (
        <RepliesItem key={`replies-item-${item.uuid}`} postUuid={postUuid} comment={item} />
      ))}
      {commentList.map((item, index) => (
        <RepliesItem
          key={`replies-item-${item.uuid}`}
          postUuid={postUuid}
          comment={item}
          onViewChange={isInView => {
            if (isRootComment) loadMoreItems(isInView, index);
          }}
        />
      ))}
      {!isRootComment && hasMore && (
        <button
          type="button"
          disabled={!hasMore || isFetchingNextPage}
          className="text-xs leading-none font-normal text-foreground  opacity-70 hover:opacity-100"
          onClick={() => {
            if (!loadMore) setLoadMore(true);
            else fetchNextPage();
          }}
        >
          {t('view-more-replies')}
        </button>
      )}
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {isRootComment && !isFetching && !hasMore && commentList.length === 0 && <BlockNothing />}
      {isRootComment && !isFetching && !hasMore && commentList.length > 0 && <EndStatus />}
    </div>
  );
};
